import { html, type TemplateResult } from "lit";
// Figma 482:66 — the handwritten "Not for nothing" lockup, underline included.
import mantraUrl from "./mantra.webp";
// Figma 482:26 "Group 37" — the mantra note: handwritten label, curve and arrowhead
// in one piece, 324.8 x 144.7.
import mantraNoteUrl from "./mantra-note.webp";
// The mobile note is a DIFFERENT drawing, not the desktop one flipped: the label
// sits right of a curve that sweeps down-left. Supplied 2026-08-10, closing the
// gap the README listed under "Still to land".
import mantraNoteMobileUrl from "./mantra-note-mobile.webp";
// The quote as artwork, for MOBILE only — supplied 2026-08-10. Desktop keeps the
// live text, so the words exist twice; see the `__quote` rules in collage.scss.
import quoteUrl from "./quote.webp";
import valvolineLogoUrl from "./valvoline-logo.webp";
import beachUrl from "./beach.webp";
import palmUrl from "./palm-tree.webp";
import ballUrl from "./soccer-ball.webp";

import "./collage.scss";

const FAMILY =
  "During his treatment, Palmer's family was unsure as to 'why' this was happening, but " +
  "they found hope that there must be a purpose. Now, using this platform, they want to " +
  "bring hope to others and know that it was 'not for nothing.'";

// Figma 482:63 "Group 39" pairs the quote text with a hand-drawn underline vector
// (482:65, 112x7.3 at x1298) sitting under "feel joy" on the first line.
const QUOTE_START = "“I want them to ";
const QUOTE_MARKED = "feel joy.";
const QUOTE_END = " Just neon all over it, and then palm trees... (the car) is beach themed.”";

/** The same words in one string, for the mobile artwork's alt text. */
const QUOTE_PLAIN = QUOTE_START + QUOTE_MARKED + QUOTE_END;

export interface CollageProps {
  /** Overrides the bundled mantra note artwork. */
  readonly mantraNote: string;
}

/**
 * The mantra / artwork collage between the artist bio and the donation form.
 *
 * Figma desktop: mantra 428:358, "Not for nothing" lockup 421:82, family copy
 * 421:227, Valvoline logo 421:68, beach 421:26, quote 421:78, palm tree 421:71,
 * soccer ball 421:29. Mobile runs the same pieces in one column, 428:1040 onward.
 */
export function renderCollage({ mantraNote }: CollageProps): TemplateResult {
  return html`
    <section class="valvoline-collage">
      <!--
        DOM order follows the MOBILE artboard (428:1040 -> 428:878): copy,
        logo + beach, quote, palm + ball. Desktop re-places these four blocks
        into two columns via explicit grid coordinates, so neither breakpoint
        has to compromise on order.
      -->
      <div class="valvoline-collage__inner">
        <div class="valvoline-collage__copy">
          <!--
            A picture element, not two images swapped in CSS: display:none still
            downloads the file, and these are two separate drawings rather than
            one asset at two sizes. The 899px cutoff is $bp-desktop - 1.

            The mantra-note attribute overrides the DESKTOP drawing only — it was
            added for that one, and the mobile note has no artboard of its own to
            substitute.
          -->
          <picture class="valvoline-collage__mantra-note-wrap">
            <source srcset=${mantraNoteMobileUrl} media="(max-width: 899px)" />
            <img
              class="valvoline-collage__mantra-note"
              src=${mantraNote || mantraNoteUrl}
              alt="Palmer’s mantra during treatment"
            />
          </picture>

          <img class="valvoline-collage__mantra" src=${mantraUrl} alt="Not for nothing" />

          <p class="valvoline-collage__family">${FAMILY}</p>
        </div>

        <div class="valvoline-collage__tiles valvoline-collage__tiles--brand">
          <img
            class="valvoline-collage__tile valvoline-collage__tile--logo"
            src=${valvolineLogoUrl}
            alt="Valvoline"
          />
          <img
            class="valvoline-collage__tile valvoline-collage__tile--beach"
            src=${beachUrl}
            alt=""
            aria-hidden="true"
          />
        </div>

        <!--
          Both forms of the quote ship; exactly one is displayed at a time, so a
          screen reader is never handed it twice. Mobile shows the artwork (its
          alt carries the words), desktop shows the live text.
        -->
        <blockquote class="valvoline-collage__quote">
          <img class="valvoline-collage__quote-art" src=${quoteUrl} alt=${QUOTE_PLAIN} />
          <span class="valvoline-collage__quote-text">
            ${QUOTE_START}<span class="valvoline-collage__mark">${QUOTE_MARKED}</span>${QUOTE_END}
          </span>
        </blockquote>

        <div class="valvoline-collage__tiles valvoline-collage__tiles--play">
          <img
            class="valvoline-collage__tile valvoline-collage__tile--palm"
            src=${palmUrl}
            alt=""
            aria-hidden="true"
          />
          <img
            class="valvoline-collage__tile valvoline-collage__tile--ball"
            src=${ballUrl}
            alt=""
            aria-hidden="true"
          />
        </div>
      </div>
    </section>
  `;
}
